import { useState, useRef, useEffect, type ReactNode } from 'react'
import { api } from '@/lib/api'
import type { OrgItem } from '@/lib/apiTypes'
import { useOidc } from '@/auth/OidcProvider'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { Modal } from '@/shared/components/Modal'
import { Switch } from '@/shared/components/Switch'
import { TIERS, TIER_LABEL, TIER_PLAN, FEATURED_TIER, type Tier } from '@/lib/tiers'

/**
 * New-organisation wizard: name → plan → governance defaults → pipeline trust →
 * review. The org itself (name + tier) is one atomic POST; every other choice is a
 * best-effort follow-up shown on a live checklist, so a failure in one never loses
 * the org or hides what still needs doing.
 *
 * Policies are deliberately NOT one of the choices. The org is created with none and the
 * feature switched on, and the Policies page offers a curated set the first time it is opened -
 * a preset picked here, before the org exists, was a set nobody read.
 */

type VcsProvider = 'github' | 'gitlab'
interface TrustEntry { provider: VcsProvider; subject: string }

interface WizardState {
  name: string
  tier: Tier
  digest: boolean
  claimDomain: string   // domain to auto-join (empty = off)
  trust: TrustEntry[]
}

type Phase = 'editing' | 'creating' | 'followups' | 'done'

interface Props {
  onClose: () => void
  /** Called once the org exists and follow-ups have settled (or the user chose to open it). */
  onCreated: (org: OrgItem) => void | Promise<void>
}

const STEPS = [
  { label: 'Name',           title: 'Name your organisation' },
  { label: 'Plan',           title: 'Choose a plan' },
  { label: 'Defaults',       title: 'Governance defaults' },
  { label: 'Pipeline trust', title: 'Trust your pipelines' },
  { label: 'Review',         title: 'Review & create' },
] as const

// ── Client mirrors of server validation (server stays authoritative) ─────────

/** Mirrors TenantService.slugify: preview only - the server may append `-N` on collision. */
function previewSlug(name: string): string {
  const s = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63)
  return new Set(['www', 'api', 'app']).has(s) ? `${s}-org` : s || 'org'
}

/** Mirrors worker lib/vcsTrustMatch.validateSubject for early feedback. */
const SUBJECT_PATTERNS: Record<VcsProvider, RegExp> = {
  github: /^repo:[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.*-]+$/,
  gitlab: /^project_path:[a-zA-Z0-9_.*/+-]+\/[a-zA-Z0-9_.*+-]+$/,
}
const CLAIM_KEY: Record<VcsProvider, string> = { github: 'repo:', gitlab: 'project_path:' }
const SUBJECT_PLACEHOLDER: Record<VcsProvider, string> = {
  github: 'repo:owner/repo  or  repo:owner/*',
  gitlab: 'project_path:group/project  or  project_path:group/*',
}

function subjectError(provider: VcsProvider, subject: string): string | null {
  if (!subject) return 'Subject is required.'
  if (subject.length > 256) return 'Subject is too long (max 256 characters).'
  if (!SUBJECT_PATTERNS[provider].test(subject)) {
    return provider === 'github'
      ? 'Use repo:owner/repo, repo:owner/* or repo:owner/*-suffix. "*" matches within one path segment.'
      : 'Use project_path:group/project, project_path:group/* or project_path:group/*-suffix. "*" matches within one path segment.'
  }
  const path = subject.slice(CLAIM_KEY[provider].length)
  if (path.includes('**')) return 'Use a single "*" per segment - "**" is not allowed.'
  const segments = path.split('/')
  if (segments.length < 2 || segments.some((seg) => seg.length === 0)) return 'Subject needs at least owner/name with no empty segments.'
  return null
}

// ── Follow-up checklist ───────────────────────────────────────────────────────

type RowStatus = 'pending' | 'running' | 'ok' | 'failed' | 'info'
interface Row {
  key: string
  label: string
  /** Where to finish this by hand if it fails. */
  fixHint?: string
  run?: () => Promise<void>
  status: RowStatus
  message?: string
}

function buildRows(state: WizardState, slug: string): Row[] {
  const rows: Row[] = [
    { key: 'org', label: `Organisation "${state.name}" on ${TIER_LABEL[state.tier]}`, status: 'ok' },
    // The org starts with no policies and the policy feature on; the curated set is chosen on
    // the Policies page, which offers it unprompted, rather than guessed at from here.
    { key: 'policies', label: 'Policies enabled - pick a recommended set on the Policies page', status: 'info' },
  ]
  if (state.digest) rows.push({
    key: 'digest', label: 'Weekly provider update digest', status: 'pending', fixHint: 'Org settings → Notifications',
    run: () => api.patch(`/api/orgs/${slug}/notifications`, { provider_update_notifications: 'weekly' }),
  })
  return rows
}

/** Normalise a typed domain: trim, lowercase, drop a leading '@'. */
function normaliseDomain(raw: string): string {
  return raw.trim().toLowerCase().replace(/^@+/, '')
}
const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/

// ── Component ────────────────────────────────────────────────────────────────

export function CreateOrgWizard({ onClose, onCreated }: Props) {
  const { user } = useOidc()
  const { refetchOrgs } = useSelectedOrg()
  const emailDomain = user?.profile.email?.split('@')[1]?.toLowerCase() ?? null

  const [step, setStep] = useState(0)
  const [state, setState] = useState<WizardState>({
    name: '', tier: FEATURED_TIER,
    digest: true, claimDomain: emailDomain ?? '', trust: [],
  })
  const patch = (p: Partial<WizardState>) => setState((s) => ({ ...s, ...p }))

  // The OIDC profile can resolve after first render; seed the domain box once, but never
  // overwrite something the user has already typed or cleared.
  const domainTouchedRef = useRef(false)
  useEffect(() => {
    if (emailDomain && !domainTouchedRef.current) setState((s) => (s.claimDomain ? s : { ...s, claimDomain: emailDomain }))
  }, [emailDomain])
  const [domainError, setDomainError] = useState<string | null>(null)

  const [nameError, setNameError] = useState<string | null>(null)
  const [trustProvider, setTrustProvider] = useState<VcsProvider>('github')
  const [trustSubject, setTrustSubject] = useState('')
  const [trustError, setTrustError] = useState<string | null>(null)

  const [phase, setPhase] = useState<Phase>('editing')
  const [createError, setCreateError] = useState<string | null>(null)
  const [ambiguous, setAmbiguous] = useState(false)          // network error: org may or may not exist
  const [recovered, setRecovered] = useState<OrgItem | null>(null)
  const [checking, setChecking] = useState(false)
  const [createdOrg, setCreatedOrg] = useState<OrgItem | null>(null)
  const [rows, setRows] = useState<Row[]>([])

  // In-flight guard (a double click must not create two orgs) + unmount guard
  // (browser back mid-sequence must not update state or fire onCreated late).
  const submitRef = useRef(false)
  const mountedRef = useRef(true)
  // Set true in the effect body, not just at init: StrictMode (dev) runs cleanup then
  // setup again on mount, so a cleanup-only effect would leave this false forever and
  // every `if (!mountedRef.current) return` guard below would silently abandon the
  // sequence right after the create request - a 201 with the modal stuck spinning.
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const slug = previewSlug(state.name)
  const claimDomain = normaliseDomain(state.claimDomain)

  // ── Step navigation ──
  function next() {
    if (step === 0 && !state.name.trim()) { setNameError('Name is required.'); return }
    if (step === 2) {
      const d = normaliseDomain(state.claimDomain)
      if (d && !DOMAIN_RE.test(d)) { setDomainError('Enter a bare domain like acme.com, or clear the box to leave auto-join off.'); return }
      if (d !== state.claimDomain) patch({ claimDomain: d })
    }
    setStep((n) => Math.min(n + 1, STEPS.length - 1))
  }
  function back() { setStep((n) => Math.max(n - 1, 0)) }

  function addTrust() {
    const subject = trustSubject.trim()
    const err = subjectError(trustProvider, subject)
    if (err) { setTrustError(err); return }
    if (state.trust.some((t) => t.provider === trustProvider && t.subject === subject)) { setTrustError('Already added.'); return }
    patch({ trust: [...state.trust, { provider: trustProvider, subject }] })
    setTrustSubject('')
    setTrustError(null)
  }

  // ── Create + follow-ups ──
  function setRow(key: string, update: Partial<Row>) {
    if (!mountedRef.current) return
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...update } : r)))
  }

  async function create() {
    if (submitRef.current) return
    submitRef.current = true
    setPhase('creating')
    setCreateError(null)
    setAmbiguous(false)

    let res: Awaited<ReturnType<typeof api.createOrg>>
    try {
      res = await api.createOrg({ name: state.name.trim(), tier: state.tier })
    } catch (err) {
      if (!mountedRef.current) return
      const status = (err as { status?: number }).status
      // No HTTP status = the request never got a response. The org may still have
      // been created; a blind retry would make a second one.
      setAmbiguous(!status)
      setCreateError(status
        ? (err as Error).message
        : "Couldn't confirm whether the organisation was created. Check your org list before trying again.")
      setPhase('editing')
      submitRef.current = false
      return
    }
    if (!mountedRef.current) return

    const org = res.org
    if (!org?.slug) {
      // Malformed success body - the org may exist but we can't drive the follow-ups.
      setCreateError('The organisation was created but the response was not understood. Refresh your org list.')
      setAmbiguous(true)
      setPhase('editing')
      submitRef.current = false
      return
    }
    setCreatedOrg(org)
    const initial = buildRows(state, org.slug)
    if (claimDomain) initial.push({
      key: 'domain', label: `Domain auto-join for @${claimDomain}`, status: 'pending', fixHint: 'Org settings → Members',
      run: () => api.post(`/api/orgs/${org.slug}/claim-domain`, { domain: claimDomain }),
    })
    for (const t of state.trust) initial.push({
      key: `trust:${t.provider}:${t.subject}`, label: `Trust ${t.provider === 'github' ? 'GitHub' : 'GitLab'} ${t.subject}`, status: 'pending', fixHint: 'Org settings → Pipeline Trust',
      run: () => api.post(`/api/orgs/${org.slug}/vcs-trust`, { provider: t.provider, subject: t.subject }),
    })
    setRows(initial)
    setPhase('followups')

    let allOk = true
    for (const row of initial) {
      if (!row.run) continue
      setRow(row.key, { status: 'running' })
      try {
        await row.run()
        setRow(row.key, { status: 'ok' })
      } catch (err) {
        allOk = false
        setRow(row.key, { status: 'failed', message: (err as Error).message })
      }
      if (!mountedRef.current) return
    }
    setPhase('done')
    if (allOk) {
      // Brief beat so the ticks register before the app switches org.
      setTimeout(() => { if (mountedRef.current) void onCreated(org) }, 500)
    }
  }

  /** After an ambiguous network failure: see whether the org actually landed. */
  async function checkOrgList() {
    setChecking(true)
    const fresh = await refetchOrgs()
    if (!mountedRef.current) return
    setChecking(false)
    const match = fresh?.orgs.find((o) => o.name === state.name.trim()) ?? null
    setRecovered(match)
    if (!match) setCreateError('Not in your org list yet - it is safe to try again.')
  }

  // ── Rendering ──
  const busy = phase === 'creating' || phase === 'followups'
  const editing = phase === 'editing'

  const footer = editing ? (
    <div style={s.footerRow}>
      <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
      <div style={{ flex: 1 }} />
      {step > 0 && <button type="button" className="btn" onClick={back}>← Back</button>}
      {step < STEPS.length - 1 ? (
        <button type="button" className="btn btn-primary" onClick={next}>
          {step === 3 && state.trust.length === 0 ? 'Skip →' : 'Next →'}
        </button>
      ) : recovered ? (
        <button type="button" className="btn btn-primary" onClick={() => void onCreated(recovered)}>Open {recovered.name} →</button>
      ) : ambiguous ? (
        <button type="button" className="btn btn-primary" onClick={checkOrgList} disabled={checking}>
          {checking ? <span className="spinner" /> : 'Refresh org list'}
        </button>
      ) : (
        <button type="button" className="btn btn-primary" onClick={create}>Create organisation</button>
      )}
    </div>
  ) : (
    <div style={s.footerRow}>
      <div style={{ flex: 1 }} />
      {phase === 'done' && createdOrg && (
        <button type="button" className="btn btn-primary" onClick={() => void onCreated(createdOrg)}>Open organisation →</button>
      )}
      {busy && <span className="spinner" />}
    </div>
  )

  return (
    <Modal
      track="create_org_wizard"
      title={editing ? STEPS[step].title : (phase === 'done' ? 'Organisation created' : 'Creating organisation…')}
      kicker={editing ? `// new organisation · step ${step + 1} of ${STEPS.length}` : '// new organisation'}
      onClose={onClose}
      maxWidth={760}
      dismissable={editing}
      footer={footer}
    >
      {editing && <StepIndicator current={step} />}

      {editing && step === 0 && (
        <div style={s.stack}>
          <div>
            <label className="field-label" htmlFor="org-wizard-name">Name</label>
            <input
              id="org-wizard-name"
              className="input"
              placeholder="Acme Corp"
              value={state.name}
              onChange={(e) => { patch({ name: e.target.value }); setNameError(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); next() } }}
              autoFocus
            />
            {nameError && <div style={s.fieldError}>{nameError}</div>}
          </div>
          {state.name.trim() && (
            <div style={s.hintBox}>
              Slug: <code style={s.code}>{slug}</code>
              <span style={s.hintMuted}> - used in URLs and as your registry namespace. If it's taken you'll get a numeric suffix.</span>
            </div>
          )}
        </div>
      )}

      {editing && step === 1 && (
        <div style={s.stack}>
          <div style={s.betaBanner}>
            <strong>No card, no commitment.</strong> Pick the
            tier that matches how you'll use Terramantle; you can upgrade any time under Billing.
          </div>
          <div role="radiogroup" aria-label="Plan" style={s.tierGrid}>
            {TIERS.map((tier) => {
              const active = state.tier === tier
              const plan = TIER_PLAN[tier]
              return (
                <button
                  key={tier}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => patch({ tier })}
                  className="tier-card"
                  style={s.tierCard}
                >
                  <div style={s.tierEyebrow}>{plan.eyebrow}</div>
                  <div style={s.tierHead}>
                    <span style={s.tierName}>{plan.name}</span>
                    {plan.featured && <span className="badge badge-amber" style={s.badgeSm}>Featured</span>}
                  </div>
                  <div style={s.tierPriceRow}>
                    <span style={s.tierPrice}>{plan.price}</span>
                    <span style={s.tierCadence}>{plan.cadence}</span>
                  </div>
                  <div style={s.tierSummary}>{plan.summary}</div>
                  <ul style={s.featureList}>
                    {plan.features.map((f) => (
                      <li key={f.label} style={s.featureItem}>
                        <span style={f.included ? s.tick : s.cross}>{f.included ? '✓' : '-'}</span>{f.label}
                      </li>
                    ))}
                  </ul>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {editing && step === 2 && (
        <div style={s.stack}>
          {/* No policy choice here. The org is created with none and the feature switched on,
              and the Policies page offers a curated set the moment it is opened - picking from a
              list of named gates you can read beats picking a preset you cannot. */}
          <div>
            <div style={s.sectionLabel}>// defaults</div>
            <ToggleRow
              label="Weekly provider update digest"
              sublabel="One email a week summarising new upstream provider versions and patched releases."
              on={state.digest} onToggle={(v) => patch({ digest: v })}
            />
            <div style={s.toggleRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={s.toggleLabel} htmlFor="org-wizard-domain">Domain auto-join</label>
                <div style={s.toggleSub}>
                  Anyone who signs in with an address at this domain is added to this org as a reader automatically.
                  Only one organisation can claim a domain. Freemail domains are rejected.
                </div>
              </div>
              <div style={s.domainField}>
                <input
                  id="org-wizard-domain"
                  className="input"
                  placeholder={emailDomain ?? 'acme.com'}
                  value={state.claimDomain}
                  onChange={(e) => { domainTouchedRef.current = true; patch({ claimDomain: e.target.value }); setDomainError(null) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); next() } }}
                  spellCheck={false}
                  autoCapitalize="none"
                />
                {domainError && <div style={s.fieldError}>{domainError}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && step === 3 && (
        <div style={s.stack}>
          <p style={s.para}>
            Trusted repos authenticate with native runner tokens in GitHub Actions and GitLab CI already
            issued - no bot tokens or stored secrets. Optional: you can add these later under Org settings → Pipeline Trust.
            The <a href="/integrations/oidc-trust" target="_blank" rel="noreferrer" style={s.link}>Pipeline Trust guide ↗</a> has the CI snippets.
          </p>
          <div style={s.trustForm}>
            <div>
              <label className="field-label" htmlFor="org-wizard-provider">Provider</label>
              <select id="org-wizard-provider" className="input" value={trustProvider}
                onChange={(e) => { setTrustProvider(e.target.value as VcsProvider); setTrustError(null) }}>
                <option value="github">GitHub</option>
                <option value="gitlab">GitLab</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="org-wizard-subject">Subject</label>
              <input
                id="org-wizard-subject"
                className="input"
                placeholder={SUBJECT_PLACEHOLDER[trustProvider]}
                value={trustSubject}
                onChange={(e) => { setTrustSubject(e.target.value); setTrustError(null) }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTrust() } }}
              />
              {trustError && <div style={s.fieldError}>{trustError}</div>}
            </div>
            <div style={s.trustAddCol}>
              <button type="button" className="btn" onClick={addTrust}>+ Add</button>
            </div>
          </div>
          {state.trust.length > 0 && (
            <ul style={s.trustList}>
              {state.trust.map((t) => (
                <li key={`${t.provider}:${t.subject}`} style={s.trustItem}>
                  <span style={s.trustProvider}>{t.provider === 'github' ? 'GitHub' : 'GitLab'}</span>
                  <code style={s.code}>{t.subject}</code>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                    onClick={() => patch({ trust: state.trust.filter((x) => x !== t) })}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editing && step === 4 && (
        <div style={s.stack}>
          <dl style={s.reviewList}>
            <ReviewRow k="Name" v={<>{state.name.trim()} <span style={s.hintMuted}>({slug})</span></>} />
            <ReviewRow k="Plan" v={TIER_LABEL[state.tier]} />
            <ReviewRow k="Policies" v="Enabled, none yet - choose a recommended set on the Policies page" />
            <ReviewRow k="Provider digest" v={state.digest ? 'Weekly' : 'Off'} />
            <ReviewRow k="Domain auto-join" v={claimDomain ? `@${claimDomain} → reader` : 'Off'} />
            <ReviewRow k="Pipeline trust" v={state.trust.length === 0 ? 'None (add later)' : (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {state.trust.map((t) => <li key={`${t.provider}:${t.subject}`}><code style={s.code}>{t.subject}</code> <span style={s.hintMuted}>({t.provider})</span></li>)}
              </ul>
            )} />
          </dl>
          {createError && <div className="feedback-error">{createError}</div>}
          {recovered && <div className="feedback-success">"{recovered.name}" is in your org list - it was created.</div>}
        </div>
      )}

      {!editing && (
        <div style={s.stack}>
          <ul style={s.checklist}>
            {rows.map((r) => (
              <li key={r.key} style={s.checkItem}>
                <span style={{ ...s.checkIcon, ...(r.status === 'ok' ? s.ok : r.status === 'failed' ? s.fail : {}) }}>
                  {r.status === 'ok' ? '✓' : r.status === 'failed' ? '✗' : r.status === 'running' ? <span className="spinner" /> : r.status === 'info' ? 'ⓘ' : '·'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={s.checkLabel}>{r.label}</div>
                  {r.message && <div style={r.status === 'failed' ? s.checkFail : s.hintMuted}>{r.message}</div>}
                  {r.status === 'failed' && r.fixHint && <div style={s.hintMuted}>Finish this under {r.fixHint}.</div>}
                </div>
              </li>
            ))}
          </ul>
          {phase === 'done' && rows.some((r) => r.status === 'failed') && (
            <div style={s.noteBox}>
              The organisation exists. Some defaults couldn't be applied - the rows above say where to finish them.
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <ol style={s.steps} aria-label="Progress">
      {STEPS.map((st, i) => {
        const done = i < current
        const active = i === current
        return (
          <li key={st.label} style={s.stepItem} aria-current={active ? 'step' : undefined}>
            <span style={{ ...s.stepNum, ...(active ? s.stepNumActive : done ? s.stepNumDone : {}) }}>{done ? '✓' : i + 1}</span>
            <span style={{ ...s.stepLabel, ...(active ? s.stepLabelActive : {}) }}>{st.label}</span>
            {i < STEPS.length - 1 && <span style={s.stepLine} aria-hidden="true" />}
          </li>
        )
      })}
    </ol>
  )
}

function ToggleRow({ label, sublabel, on, onToggle }: { label: string; sublabel: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <div style={s.toggleRow}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.toggleLabel}>{label}</div>
        <div style={s.toggleSub}>{sublabel}</div>
      </div>
      <Switch checked={on} onChange={onToggle} label={label} />
    </div>
  )
}

function ReviewRow({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={s.reviewRow}>
      <dt style={s.reviewKey}>{k}</dt>
      <dd style={s.reviewVal}>{v}</dd>
    </div>
  )
}

// ── Styles (design tokens only - see lint:colors) ─────────────────────────────

const s: Record<string, React.CSSProperties> = {
  stack: { display: 'grid', gap: 18 },
  footerRow: { display: 'flex', alignItems: 'center', gap: 8 },
  fieldError: { marginTop: 4, fontSize: '0.72rem', color: 'var(--red)' },
  para: { fontFamily: 'var(--body)', fontSize: '0.85rem', color: 'var(--text2)', lineHeight: 1.6, margin: 0 },
  link: { color: 'var(--accent)', textDecoration: 'none' },
  code: { fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--accent)' },
  hintBox: { fontFamily: 'var(--body)', fontSize: '0.8rem', color: 'var(--text2)', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', lineHeight: 1.5 },
  hintMuted: { fontSize: '0.74rem', color: 'var(--text3)' },
  sectionLabel: { fontFamily: 'var(--mono)', fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 8 },
  betaBanner: { background: 'var(--accent-d)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', fontFamily: 'var(--body)', fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.5 },
  noteBox: { marginTop: 10, background: 'var(--amber-d)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', fontFamily: 'var(--body)', fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5 },

  // steps
  steps: { display: 'flex', alignItems: 'center', gap: 0, listStyle: 'none', margin: '0 0 18px', padding: 0 },
  stepItem: { display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 },
  stepNum: { width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: '0.68rem', background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text3)', flexShrink: 0 },
  stepNumActive: { background: 'var(--accent-d)', borderColor: 'var(--accent)', color: 'var(--accent)' },
  stepNumDone: { background: 'var(--green-d)', borderColor: 'var(--green)', color: 'var(--green)' },
  stepLabel: { marginLeft: 6, fontFamily: 'var(--mono)', fontSize: '0.64rem', letterSpacing: '0.04em', color: 'var(--text3)', whiteSpace: 'nowrap' },
  stepLabelActive: { color: 'var(--text)' },
  stepLine: { flex: 1, height: 1, background: 'var(--border)', margin: '0 10px', minWidth: 8 },

  // tier cards
  tierGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 },
  tierCard: { padding: '12px 14px', display: 'grid', gap: 6, alignContent: 'start' },
  tierHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  tierName: { fontFamily: 'var(--head)', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text)' },
  badgeSm: { fontSize: '0.55rem' },
  tierEyebrow: { fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text3)' },
  tierSummary: { fontFamily: 'var(--body)', fontSize: '0.72rem', color: 'var(--text2)', lineHeight: 1.45 },
  tierPriceRow: { display: 'flex', alignItems: 'baseline', gap: 4 },
  tierPrice: { fontFamily: 'var(--mono)', fontSize: '0.85rem', color: 'var(--accent)' },
  tierCadence: { fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)' },
  featureList: { margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 3 },
  featureItem: { fontSize: '0.72rem', color: 'var(--text2)', display: 'flex', alignItems: 'baseline', gap: 6, lineHeight: 1.35 },
  tick: { color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: '0.65rem', flexShrink: 0 },
  cross: { color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.65rem', flexShrink: 0 },

  // toggles
  toggleRow: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '10px 0', borderTop: '1px solid var(--border)' },
  toggleLabel: { fontSize: '0.85rem', color: 'var(--text)' },
  toggleSub: { fontSize: '0.72rem', color: 'var(--text3)', marginTop: 2, lineHeight: 1.45 },
  domainField: { width: 220, flexShrink: 0 },

  // trust
  trustForm: { display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' },
  trustAddCol: { display: 'flex', alignItems: 'flex-end', alignSelf: 'stretch', paddingTop: 22 },
  trustList: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 },
  trustItem: { display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' },
  trustProvider: { fontFamily: 'var(--mono)', fontSize: '0.66rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' },

  // review + checklist
  reviewList: { margin: 0, display: 'grid', gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' },
  reviewRow: { display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' },
  reviewKey: { fontFamily: 'var(--mono)', fontSize: '0.66rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text3)', margin: 0, paddingTop: 2 },
  reviewVal: { fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text)', margin: 0, lineHeight: 1.5 },
  checklist: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 },
  checkItem: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  checkIcon: { width: 20, display: 'inline-flex', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text3)', flexShrink: 0, paddingTop: 1 },
  ok: { color: 'var(--green)' },
  fail: { color: 'var(--red)' },
  checkLabel: { fontFamily: 'var(--body)', fontSize: '0.84rem', color: 'var(--text)' },
  checkFail: { fontSize: '0.74rem', color: 'var(--red)', marginTop: 2 },
}
